/**
 * Learn, Coach, Talk shop.
 *
 * Three cards on a desktop, one switcher on a phone, and the same three
 * groups either way. The list lives in constants/actionPaths.js; this file
 * is only how it looks and what a tap does.
 *
 * THE SECOND PASS. The first version was three identical dark rectangles
 * with lists in them, and Coleman's word for it was "generic". Four things
 * changed, all of them about giving the cards somewhere to come from:
 *
 *   A photograph each. The four category cards this replaced carried
 *   photos; these started as flat boxes. Same photos, recovered from the
 *   old cards and served as files now rather than base64 in the bundle.
 *
 *   An icon each, so the three read as three different things at a glance
 *   rather than one thing three times.
 *
 *   A lead item. Every row used to weigh the same. The first item in each
 *   card now leads, in a raised box. It carried a line from platformMap's
 *   `when` under it until 2026-10-07, when Coleman cut them as corny: the
 *   label says enough.
 *
 *   It responds. A card lifts and its edge warms under the cursor. Nothing
 *   on the first version moved, which is a big part of why it felt like a
 *   printout.
 *
 * WHY A SWITCHER ON MOBILE. Stacked, three cards cost about 570px, so a
 * coach scrolled past two of them to reach the one they wanted, every time.
 * A 44px segmented row with the chosen group under it does the same job in
 * a fraction of the height, and the pick is remembered.
 *
 * One gold at a time (playbook/builderUi.js). Nothing here is a gold fill:
 * the selected tab is the house `on` treatment, a raised neutral with a gold
 * hairline, because the single gold affordance in this band belongs to the
 * card above it.
 *
 * No em dashes.
 */
import React, { useCallback, useMemo } from "react";
import { ChevronRight, GraduationCap, ClipboardList, MessagesSquare } from "lucide-react";
import { GOLD } from "../constants/theme";
import { ACTION_PATHS, resolvePath } from "../constants/actionPaths";
import { haptic } from "../../utils/nativeBridge";
import { logEvent } from "../utils/analytics";
import { Glide } from "../shared/motion";

const R = { sm: 8, md: 10, lg: 12, xl: 14 };
const hf = "'Bebas Neue', sans-serif";
const ICONS = { learn: GraduationCap, coach: ClipboardList, talk: MessagesSquare };
const LAST_PATH_KEY = "bam_last_path";

function readLast() {
  try { return localStorage.getItem(LAST_PATH_KEY); } catch { return null; }
}

/** The photograph, with the group's icon and name set over it. */
function PhotoHead({ path, height, showLabel }) {
  const Icon = ICONS[path.icon];
  return (
    <div style={{ position: "relative", height, overflow: "hidden", flexShrink: 0 }}>
      <img src={path.art} alt="" aria-hidden="true" loading="lazy" className="bam-path-art"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", objectPosition: path.artPosition || "center 28%", display: "block" }}/>
      {/* Dark in both themes: these are dark photographs, and white type on
          them has to stay legible whether the card below is black or white. */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.38) 45%, rgba(0,0,0,0.78) 100%)" }}/>
      <div style={{ position: "absolute", left: 18, right: 18, bottom: 14,
        display: "flex", alignItems: "center", gap: 10 }}>
        {Icon && (
          <span style={{ width: 32, height: 32, borderRadius: R.md, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(10,10,10,0.55)", border: "1px solid rgba(255,255,255,0.16)",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
            <Icon size={16} color="#FFFFFF" strokeWidth={2}/>
          </span>
        )}
        {showLabel
          ? <span style={{ fontFamily: hf, fontSize: 32, letterSpacing: 1.4, lineHeight: 1,
              color: "#FFFFFF", textShadow: "0 2px 12px rgba(0,0,0,0.5)" }}>{path.label}</span>
          : <span style={{ fontSize: 13.5, fontWeight: 600, color: "rgba(255,255,255,0.92)",
              lineHeight: 1.35, textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}>{path.blurb}</span>}
      </div>
    </div>
  );
}

/** One destination. A real anchor, so a long press offers a new tab. */
function PathItem({ item, C, dark, onGo, lead, compact }) {
  const click = (e) => {
    e.preventDefault();
    haptic("selection");
    logEvent("home_path_click", { path: item.pathId, item: item.id, to: item.to, lead: !!lead });
    onGo(item.to);
  };
  return (
    <a href={`#/${item.to}`} onClick={click} className="bam-path-row"
      style={{
        display: "flex", alignItems: "center", gap: 12,
        minHeight: lead ? 54 : (compact ? 52 : 46),
        padding: lead ? "10px 14px" : (compact ? "0 16px" : "0 14px"),
        margin: lead ? "0 0 6px" : 0,
        borderRadius: lead ? R.md : 0,
        // The lead gets a raised neutral, not gold. Hierarchy without
        // spending the one gold this band is allowed.
        background: lead ? (dark ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.035)") : "transparent",
        border: lead ? `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` : "1px solid transparent",
        color: C.text, textDecoration: "none",
      }}>
      <span style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ fontSize: lead ? 16 : 15, fontWeight: lead ? 700 : 600, lineHeight: 1.25 }}>
          {item.label}
        </span>
      </span>
      <ChevronRight size={lead ? 18 : 16} color={C.textMid} style={{ flexShrink: 0 }}/>
    </a>
  );
}

/* The photo's scrim ends at 0.78 black and the card body under it is
   lighter, so the two surfaces butt and the step reads as a line (Coleman,
   2026-10-07, same seam MasterclassCard had). The body carries the scrim on
   for 18px and dissolves it, and overlaps the photo by a pixel so a
   fractional row can't show between them either. */
const SCRIM_TAIL = {
  position: "relative", marginTop: -1,
  backgroundImage: "linear-gradient(180deg, rgba(0,0,0,0.78) 0px, rgba(0,0,0,0) 18px)",
};

const Rule = ({ C, inset }) => (
  <div style={{ height: 1, background: C.border, margin: inset ? "0 16px" : "0 14px" }}/>
);

/** What is happening in the community, so Talk shop is not one lonely row.
 *
 *  Tapping it opens that post in the feed, and with more than one going it
 *  moves to the next every five seconds (Coleman, 2026-10-07). It holds
 *  still while the cursor or focus is on it, so nothing changes under a
 *  click, and with reduced motion the swap is a cut rather than a fade. */
const PEEK_EVERY_MS = 5000;

function CommunityPeek({ community, C, compact }) {
  const posts = (community && Array.isArray(community.posts) && community.posts.length)
    ? community.posts
    : (community && community.top ? [{ id: null, text: community.top, by: community.by, avatar: community.avatar }] : []);
  const [i, setI] = React.useState(0);
  const [held, setHeld] = React.useState(false);
  const many = posts.length > 1;
  React.useEffect(() => { if (i >= posts.length) setI(0); }, [i, posts.length]);
  React.useEffect(() => {
    if (!many || held) return undefined;
    const t = setInterval(() => setI(n => (n + 1) % posts.length), PEEK_EVERY_MS);
    return () => clearInterval(t);
  }, [many, held, posts.length]);

  if (!community || !community.count || !posts.length) return null;
  const post = posts[Math.min(i, posts.length - 1)];
  const initials = (post.by || "").split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]).join("").toUpperCase() || "?";
  const open = () => {
    haptic("selection");
    logEvent("home_peek_click", { post: post.id || null, index: i });
    if (post.id && community.onOpen) community.onOpen(post.id);
  };

  return (
    <button type="button" onClick={open} className="bam-path-row"
      onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}
      aria-label={`Open ${post.by ? `${post.by}'s` : "this"} post in the community`}
      style={{ display: "block", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
        background: "transparent", border: "1px solid transparent", borderRadius: R.md, color: C.text,
        margin: compact ? "2px 0 0" : "4px 0 0", padding: compact ? "12px 16px 14px" : "12px 14px 12px" }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.3, textTransform: "uppercase", color: C.textMid, flexGrow: 1 }}>
          {community.count === 1 ? "1 conversation going" : `${community.count} conversations going`}
        </span>
        {many && (
          <span aria-hidden="true" style={{ display: "flex", gap: 4 }}>
            {posts.map((p, k) => (
              <span key={p.id || k} style={{ width: k === i ? 12 : 4, height: 4, borderRadius: 2,
                background: k === i ? C.textMid : C.border, transition: "width .3s ease, background .3s ease" }}/>
            ))}
          </span>
        )}
      </span>
      <span key={post.id || i} className="bam-peek-in" style={{ display: "block" }}>
        <span style={{ display: "-webkit-box", fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 10,
          WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: "3em" }}>
          {post.text}
        </span>
        {post.by && (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 22, height: 22, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
              background: "rgba(226,221,159,0.16)", color: GOLD, fontSize: 9.5, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              {post.avatar
                ? <img src={post.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
                : initials}
            </span>
            <span style={{ fontSize: 12.5, color: C.textMid, fontWeight: 600,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{post.by}</span>
          </span>
        )}
      </span>
    </button>
  );
}

export function ActionPaths({ C, dark, go, narrow, community }) {
  const [picked, setPicked] = React.useState(() => resolvePath(readLast()).id);

  const choose = useCallback((id) => {
    setPicked(id);
    haptic("selection");
    try { localStorage.setItem(LAST_PATH_KEY, id); } catch { /* private window */ }
    logEvent("home_path_switch", { path: id });
  }, []);

  const withPath = useMemo(() => ACTION_PATHS.map(p => ({
    ...p, items: p.items.map(i => ({ ...i, pathId: p.id })),
  })), []);

  /* ── Phone: one switcher, then the chosen group ──────────────────────── */
  if (narrow) {
    const group = withPath.find(p => p.id === picked) || withPath[0];
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div role="tablist" aria-label="What do you want to do"
          style={{
            display: "flex", gap: 2, padding: 3, borderRadius: R.md, position: "relative",
            background: dark ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.05)",
            border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
          }}>
          {/* The chosen tab's lifted plate slides across rather than
              jumping (shared/motion). Same look as the "on" style below. */}
          <Glide variant="pill" style={{
            borderRadius: R.sm,
            background: dark ? "rgba(255,255,255,0.14)" : "#FFFFFF",
            boxShadow: `inset 0 0 0 1px ${GOLD}99, ${dark ? "0 2px 10px rgba(0,0,0,0.45)" : "0 1px 4px rgba(0,0,0,0.12)"}`,
          }}/>
          {withPath.map(p => {
            const on = p.id === picked;
            return (
              <button key={p.id} type="button" role="tab" aria-selected={on} data-active={on}
                onClick={() => choose(p.id)}
                style={{
                  flexGrow: 1, flexBasis: 0, display: "flex", alignItems: "center", justifyContent: "center",
                  height: 44, borderRadius: R.sm, cursor: "pointer", fontFamily: hf,
                  fontSize: 19, letterSpacing: 1.1, lineHeight: 1, padding: "0 6px",
                  // The house "on", made unmistakable. The first version used
                  // 10% white and a faint hairline, and on a phone the chosen
                  // tab read as the DISABLED one. More lift, a hairline you can
                  // see, and the unchosen tabs pushed back further.
                  background: on ? (dark ? "rgba(255,255,255,0.14)" : "#FFFFFF") : "transparent",
                  border: on ? `1px solid ${GOLD}99` : "1px solid transparent",
                  boxShadow: on ? (dark ? "0 2px 10px rgba(0,0,0,0.45)" : "0 1px 4px rgba(0,0,0,0.12)") : "none",
                  color: on ? (dark ? "#FFFFFF" : "#111111") : (dark ? "#7A7A7A" : "#8A8A8A"),
                  transition: "background .15s, color .15s, border-color .15s",
                }}>
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ borderRadius: R.xl, background: C.bgCard,
          border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <PhotoHead path={group} height={92} showLabel={false}/>
          <div style={{ ...SCRIM_TAIL, padding: "11px 10px 6px" }}>
            {group.items.map((item, i) => (
              <React.Fragment key={item.id}>
                {i > 1 && <Rule C={C} inset/>}
                <PathItem item={item} C={C} dark={dark} onGo={go} lead={i === 0} compact/>
              </React.Fragment>
            ))}
          </div>
          {group.id === "talk" && <CommunityPeek community={community} C={C} compact/>}
        </div>
      </div>
    );
  }

  /* ── Desktop: all three, side by side ────────────────────────────────── */
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 18 }}>
      {withPath.map(p => (
        <div key={p.id} className="bam-path-card" style={{
          display: "flex", flexDirection: "column", overflow: "hidden",
          borderRadius: R.xl, background: C.bgCard, border: `1px solid ${C.border}`,
        }}>
          <PhotoHead path={p} height={128} showLabel/>
          <div style={{ ...SCRIM_TAIL, padding: "15px 12px 10px", display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <div style={{ fontSize: 13, color: C.textMid, margin: "0 2px 12px", lineHeight: 1.45 }}>{p.blurb}</div>
            {p.items.map((item, i) => (
              <React.Fragment key={item.id}>
                {i > 1 && <Rule C={C}/>}
                <PathItem item={item} C={C} dark={dark} onGo={go} lead={i === 0}/>
              </React.Fragment>
            ))}
            {p.id === "talk" && <CommunityPeek community={community} C={C}/>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ActionPaths;
